import { useCallback, useMemo } from 'react'

import { type SelectValue } from '@procurify/ui'
import { useFormikContext } from 'formik'

import {
  type GlobalCommandOrgCategoriesFormSchema,
  type IGlobalCommandOrgCategoriesFormPrimaryCategory,
  type IGlobalCommandOrgCategoriesFormSecondaryCategory,
} from '../../types'

const toPrimaryCategoryOption = (
  primaryCategory: IGlobalCommandOrgCategoriesFormPrimaryCategory
): SelectValue<IGlobalCommandOrgCategoriesFormPrimaryCategory> => ({
  label: primaryCategory.name,
  value: primaryCategory,
})

const toSecondaryCategoryOption = (
  secondaryCategory: IGlobalCommandOrgCategoriesFormSecondaryCategory
): SelectValue<IGlobalCommandOrgCategoriesFormSecondaryCategory> => ({
  label: secondaryCategory.name,
  value: secondaryCategory,
})

export const useGlobalCommandOrgCategoriesForm = ({
  primaryCategories,
}: {
  primaryCategories: IGlobalCommandOrgCategoriesFormPrimaryCategory[]
}) => {
  const { values, setFieldValue } =
    useFormikContext<GlobalCommandOrgCategoriesFormSchema>()

  const primaryCategoryOptions = useMemo(
    () => primaryCategories.map(toPrimaryCategoryOption),
    [primaryCategories]
  )

  const secondaryCategoryOptions = useMemo(
    () =>
      (values.primaryCategory?.value.secondaryCategories ?? []).map(
        toSecondaryCategoryOption
      ),
    [values.primaryCategory]
  )

  const isSecondaryCategoryDisabled = secondaryCategoryOptions.length === 0

  // The second dropdown belongs to the selection the agent offers: it shows
  // while nothing is picked yet, and once a pick carries options of its own.
  const isSecondaryCategoryVisible =
    !values.primaryCategory || secondaryCategoryOptions.length > 0

  const handlePrimaryCategoryChange = useCallback(
    (
      option: Nullable<
        SelectValue<IGlobalCommandOrgCategoriesFormPrimaryCategory>
      >
    ) => {
      if (option?.value.id === values.primaryCategory?.value.id) {
        return
      }

      setFieldValue('primaryCategory', option ?? null)
      setFieldValue('secondaryCategory', null)
    },
    [values.primaryCategory, setFieldValue]
  )

  return {
    primaryCategoryOptions,
    secondaryCategoryOptions,
    isSecondaryCategoryVisible,
    isSecondaryCategoryDisabled,
    handlePrimaryCategoryChange,
  }
}
